import { useState } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthImg } from "@/components/AuthImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Images, Fingerprint, Copy, Play, Loader2, CheckCircle2, AlertTriangle, ShieldCheck, Truck, Package, ImageOff, ShoppingCart, FileSearch, Link2Off, ListChecks, HardDriveDownload , Maximize2} from "lucide-react";

function StatRow({ label, value, tone = "slate" }) {
  const c = { slate: "text-slate-700", amber: "text-[#C76714]", green: "text-emerald-700", red: "text-[#B01E28]" }[tone];
  return (
    <div className="flex items-center justify-between text-sm py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className={`font-mono font-semibold ${c}`}>{value}</span>
    </div>
  );
}

export default function PhotoMaintenancePage() {
  const { has } = useAuth();
  const canManageParts = has("manage_parts");
  const canManageBackups = has("manage_backups");
  const [dur, setDur] = useBackState("dur", null);           // photos with no durable copy
  const [durBusy, setDurBusy] = useState(false);
  const [shot, setShot] = useState(null);   // the one photo viewer
  const [bfRunning, setBfRunning] = useState(false);
  const [bf, setBf] = useBackState("bf", null);              // backfill result
  const [scanning, setScanning] = useState(false);
  const [report, setReport] = useBackState("report", null);      // duplicates report
  const [resolvingId, setResolvingId] = useState(null);
  const [keepMap, setKeepMap] = useState({});       // `${type}:${id}:${hash}` -> chosen keep file_id
  const [msBusy, setMsBusy] = useState(false);
  const [ms, setMs] = useBackState("ms", null);               // missing-file scan result
  const [gapBusy, setGapBusy] = useState(false);
  const [gaps, setGaps] = useBackState("gaps", null);           // signed-off photo gaps
  const [counts, setCounts] = useBackState("counts", null);       // source-tie diagnostics
  const [countsBusy, setCountsBusy] = useState(false);
  const [awaiting, setAwaiting] = useBackState("awaiting", null);   // parts awaiting a source truck
  const [awaitBusy, setAwaitBusy] = useState(false);
  const [stale, setStale] = useBackState("stale", null);         // installed-but-available stale-status parts
  const [staleBusy, setStaleBusy] = useState(false);

  const loadDurability = async () => {
    setDurBusy(true);
    try {
      const { data } = await api.get("/admin/photos/no-durable-copy", { timeout: 300000 });
      setDur(data);
      toast[data.total ? "warning" : "success"](data.total
        ? `${data.total} photo(s) have no durable backup copy.`
        : "Every photo has a durable copy.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load."); }
    finally { setDurBusy(false); }
  };

  const repairDurability = async () => {
    if (!window.confirm("Upload every local-only photo to durable cloud storage? Nothing is deleted. A photo whose image file no longer exists anywhere is marked LOST — kept and labelled, never silently hidden and never offered as restorable.")) return;
    setDurBusy(true);
    try {
      const { data } = await api.post("/admin/photos/make-durable", null,
        { params: { apply: true }, timeout: 600000 });
      toast.success(`${data.healed} photo(s) made durable${data.lost ? ` · ${data.lost} marked lost (bytes already gone)` : ""}${data.remaining ? ` · ${data.remaining} still to do — run again` : ""}.`);
      await loadDurability();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Repair failed."); }
    finally { setDurBusy(false); }
  };

  const loadStale = async () => {
    setStaleBusy(true);
    try { const { data } = await api.get("/carts/reports/stale-status", { timeout: 120000 }); setStale(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setStaleBusy(false); }
  };

  const loadCounts = async () => {
    setCountsBusy(true);
    try { const { data } = await api.get("/admin/inventory/source-tie-counts", { timeout: 120000 }); setCounts(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load counts."); }
    finally { setCountsBusy(false); }
  };
  const loadAwaiting = async () => {
    setAwaitBusy(true);
    try {
      const { data } = await api.get("/admin/inventory/awaiting-source-tie", { timeout: 120000 });
      setAwaiting(data);
      toast[data.total ? "warning" : "success"](data.total
        ? `${data.total} part(s) awaiting a source truck.`
        : "No parts awaiting a source truck — you're at zero.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load list."); }
    finally { setAwaitBusy(false); }
  };

  const REASON_LABEL = {
    no_photo_on_record: "No photo was ever taken",
    photo_bytes_missing: "Photo file is missing (phantom)",
    only_archived_photos: "Only archived photos remain",
  };
  const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");

  const runMissingScan = async (apply) => {
    if (apply && !window.confirm("Flag photos whose files are definitely missing? This only annotates records so they surface on Home and stop counting as photographed / ship-ready. Nothing is deleted; a flag clears itself if the file returns.")) return;
    setMsBusy(true);
    try {
      const { data } = await api.post("/admin/photos/scan-missing", null, { params: { apply }, timeout: 300000 });
      setMs(data);
      const n = data.flagged;
      toast[n ? "warning" : "success"](apply
        ? (n ? `${n} photo(s) flagged as missing.` : "No missing photos to flag.")
        : (n ? `Dry run — ${n} photo(s) would be flagged.` : "Dry run — no missing photos found."));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed."); }
    finally { setMsBusy(false); }
  };

  const loadGaps = async () => {
    setGapBusy(true);
    try {
      const { data } = await api.get("/carts/reports/signed-off-photo-gaps", { timeout: 120000 });
      setGaps(data);
      toast[data.count ? "warning" : "success"](data.count
        ? `${data.count} signed-off cart(s) with a photo gap.`
        : "No signed-off carts with photo gaps.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load report."); }
    finally { setGapBusy(false); }
  };

  const runBackfill = async (apply) => {
    if (apply && !window.confirm("Apply the content-hash backfill? This reads each stored photo and writes only its hash. Safe & idempotent.")) return;
    setBfRunning(true);
    try {
      const { data } = await api.post("/admin/photo-hash-backfill", null, { params: { apply }, timeout: 300000 });
      setBf(data);
      toast.success(apply ? `Backfill applied — ${data.totals.hashed} photo hash(es) written.` : `Dry run — ${data.totals.would_hash} photo(s) need hashing.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backfill failed."); }
    finally { setBfRunning(false); }
  };

  const scanDuplicates = async () => {
    setScanning(true);
    try {
      const { data } = await api.get("/admin/photo-duplicates", { timeout: 300000 });
      setReport(data); setKeepMap({});
      const total = data.totals.truck_extra_copies + data.totals.part_extra_copies;
      toast[total ? "warning" : "success"](total ? `${total} extra copy(ies) found across ${data.totals.truck_records + data.totals.part_records} record(s).` : "No exact duplicates found.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed."); }
    finally { setScanning(false); }
  };

  const keepFor = (rec, g) => keepMap[`${rec.entity_type}:${rec.id}:${g.content_hash}`]
    || (g.photos.find((p) => p.is_kept) || g.photos[0]).file_id;

  const archiveListFor = (rec) => rec.groups.flatMap((g) => {
    const keep = keepFor(rec, g);
    return g.photos.filter((p) => p.file_id !== keep).map((p) => p.file_id);
  });

  const resolveRecord = async (rec) => {
    const archive_file_ids = archiveListFor(rec);
    if (!archive_file_ids.length) return;
    if (!window.confirm(`Soft-archive ${archive_file_ids.length} duplicate copy(ies) on ${rec.label}? One copy of each photo is kept. Archived photos are hidden (not deleted) and logged.`)) return;
    setResolvingId(rec.id);
    try {
      const { data } = await api.post("/admin/photo-duplicates/resolve", {
        entity_type: rec.entity_type, entity_id: rec.id, archive_file_ids,
      });
      toast.success(`${data.archived_count} duplicate(s) archived on ${rec.label}.`);
      await scanDuplicates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not archive duplicates."); }
    finally { setResolvingId(null); }
  };

  const renderRecord = (rec) => (
    <div key={`${rec.entity_type}-${rec.id}`} data-testid={`dup-record-${rec.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-3">
        {rec.entity_type === "truck" ? <Truck className="w-4 h-4 text-[#C76714]" /> : <Package className="w-4 h-4 text-[#C76714]" />}
        <span className="font-mono font-semibold text-slate-800">{rec.label}</span>
        <Badge className="rounded-full border-0 bg-amber-100 text-[#C76714] text-[10px] font-mono">{rec.extra_copies} extra{rec.extra_copies === 1 ? "" : "s"}</Badge>
        <Button data-testid={`dup-resolve-${rec.id}`} size="sm" disabled={resolvingId === rec.id}
          onClick={() => resolveRecord(rec)} className="ml-auto rounded-lg bg-[#B01E28] hover:bg-[#8f1820] text-white h-8">
          {resolvingId === rec.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" />Keep one, archive {archiveListFor(rec).length}</>}
        </Button>
      </div>
      <div className="space-y-3">
        {rec.groups.map((g) => {
          const keep = keepFor(rec, g);
          return (
            <div key={g.content_hash} data-testid={`dup-group-${g.content_hash.slice(0, 10)}`} className="flex flex-wrap gap-2 items-start border-t border-slate-100 pt-2 first:border-t-0 first:pt-0">
              {g.photos.map((p) => {
                const isKeep = p.file_id === keep;
                return (
                  <div key={p.file_id} className="relative">
                  <button type="button" title={isKeep ? "Kept" : "Click to keep this one instead"}
                    data-testid={`dup-photo-${p.file_id.slice(0, 8)}`}
                    onClick={() => setKeepMap((m) => ({ ...m, [`${rec.entity_type}:${rec.id}:${g.content_hash}`]: p.file_id }))}
                    className={`relative rounded-lg overflow-hidden border-2 ${isKeep ? "border-emerald-500" : "border-slate-200 opacity-70 hover:opacity-100"}`}>
                    <AuthImg fileId={p.file_id} thumb className="w-20 h-20 object-cover" alt="duplicate" />
                    <span className={`absolute bottom-0 inset-x-0 text-[9px] font-bold text-center py-0.5 ${isKeep ? "bg-emerald-600 text-white" : "bg-[#B01E28]/80 text-white"}`}>
                      {isKeep ? "KEEP" : "archive"}
                    </span>
                    {p.assigned && <span className="absolute top-0.5 right-0.5 bg-blue-600 text-white text-[8px] font-bold px-1 rounded">USED</span>}
                  </button>
                  {/* A LOOK BEFORE YOU CHOOSE. Tapping the photo picks the keeper, so opening it
                      properly gets its own control — two near-identical photos is exactly when
                      somebody needs to zoom (2026-10-15). */}
                  <button type="button" title="Open this photo" data-testid={`dup-photo-open-${p.file_id.slice(0, 8)}`}
                    onClick={() => setShot({ list: g.photos, idx: g.photos.indexOf(p) })}
                    className="absolute bottom-0.5 left-0.5 inline-flex items-center justify-center w-6 h-6 rounded-md bg-white/90 text-slate-700 shadow border border-slate-200">
                    <Maximize2 className="w-3.5 h-3.5" />
                  </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );

  const t = report?.totals;

  return (
    <div data-testid="photo-maintenance-page" className="max-w-3xl">
      <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2">
        <Images className="w-6 h-6 text-[#B01E28]" />Photo Maintenance
      </h1>
      <p className="text-sm text-slate-500 mb-6">Admin tools for the photo library. Run these on the environment whose photos you want to fix (e.g. production). Both are read-first and never hard-delete.</p>

      {/* -------- BACKFILL -------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Fingerprint className="w-5 h-5 text-[#C76714]" />
          <h2 className="font-display text-lg font-bold text-slate-800">1 · Content-hash backfill</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Hashes every stored photo (trucks &amp; parts) so older photos dedup like fresh ones. Reads the stored image only to compute its hash — never re-processes, moves, or deletes anything. Idempotent.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button data-testid="backfill-dry-run" variant="outline" disabled={bfRunning} onClick={() => runBackfill(false)} className="rounded-lg h-10 border-slate-300">
            {bfRunning ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Play className="w-4 h-4 mr-1.5" />}Dry run (report only)
          </Button>
          <Button data-testid="backfill-apply" disabled={bfRunning} onClick={() => runBackfill(true)} className="rounded-lg h-10 bg-[#E0781A] hover:bg-[#C76714] text-white">
            {bfRunning ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Fingerprint className="w-4 h-4 mr-1.5" />}Apply backfill
          </Button>
        </div>
        {bf && (
          <div data-testid="backfill-result" className="rounded-xl border border-slate-200 bg-slate-50 p-4 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Trucks</div>
              <StatRow label="Photos" value={bf.donors.photos} />
              <StatRow label={bf.mode === "apply" ? "Hashed" : "Would hash"} value={bf.mode === "apply" ? bf.donors.hashed : bf.donors.would_hash} tone="green" />
              <StatRow label="Missing bytes" value={bf.donors.missing_bytes} tone={bf.donors.missing_bytes ? "red" : "slate"} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Parts</div>
              <StatRow label="Photos" value={bf.parts.photos} />
              <StatRow label={bf.mode === "apply" ? "Hashed" : "Would hash"} value={bf.mode === "apply" ? bf.parts.hashed : bf.parts.would_hash} tone="green" />
              <StatRow label="Missing bytes" value={bf.parts.missing_bytes} tone={bf.parts.missing_bytes ? "red" : "slate"} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Total ({bf.mode === "apply" ? "applied" : "dry run"})</div>
              <StatRow label={bf.mode === "apply" ? "Hashed" : "Would hash"} value={bf.mode === "apply" ? bf.totals.hashed : bf.totals.would_hash} tone="green" />
              <StatRow label="Already hashed" value={bf.totals.already_hashed} />
              <StatRow label="Missing bytes" value={bf.totals.missing_bytes} tone={bf.totals.missing_bytes ? "red" : "slate"} />
            </div>
          </div>
        )}
      </div>

      {/* -------- DUPLICATES -------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1">
          <Copy className="w-5 h-5 text-[#C76714]" />
          <h2 className="font-display text-lg font-bold text-slate-800">2 · Duplicate cleanup</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Finds photos that are the <span className="font-semibold">exact same image</span> (identical hash) on the same truck or part — usually from re-uploading a batch. Review first; keep one copy, soft-archive the extras (hidden, logged, restorable). Run the backfill above first so older photos are hashed.</p>
        <Button data-testid="scan-duplicates" disabled={scanning} onClick={scanDuplicates} className="rounded-lg h-10 bg-[#1F1F1F] hover:bg-black text-white mb-3">
          {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}Scan trucks &amp; parts for duplicates
        </Button>

        {report && (
          <div data-testid="dup-report">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1">
              <StatRow label="Truck records" value={t.truck_records} tone={t.truck_records ? "amber" : "green"} />
              <StatRow label="Truck extras" value={t.truck_extra_copies} tone={t.truck_extra_copies ? "amber" : "green"} />
              <StatRow label="Part records" value={t.part_records} tone={t.part_records ? "amber" : "green"} />
              <StatRow label="Part extras" value={t.part_extra_copies} tone={t.part_extra_copies ? "amber" : "green"} />
            </div>
            {(t.unhashed_truck_photos + t.unhashed_part_photos) > 0 && (
              <div data-testid="dup-unhashed-warning" className="rounded-lg border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-[#C76714] flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                {t.unhashed_truck_photos + t.unhashed_part_photos} photo(s) have no hash yet and were skipped — run the content-hash backfill above first, then re-scan to catch all duplicates.
              </div>
            )}
            {(t.truck_records + t.part_records) === 0 ? (
              <div data-testid="dup-none" className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-700 flex items-center justify-center gap-2">
                <ShieldCheck className="w-5 h-5" />No exact duplicates found — your photo library is clean.
              </div>
            ) : (
              <div className="space-y-4">
                {(t.truck_records > report.trucks.length || t.part_records > report.parts.length) && (
                  <p data-testid="dup-shown" className="text-sm text-slate-600">
                    Showing the first {report.trucks.length} of {t.truck_records} truck records and {report.parts.length} of {t.part_records} part records,
                    most copies first. Clean these up, then scan again for the rest.
                  </p>
                )}
                {report.trucks.map(renderRecord)}
                {report.parts.map(renderRecord)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* -------- MISSING PHOTO FILES (self-heal scan) -------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-5">
        <div className="flex items-center gap-2 mb-1">
          <ImageOff className="w-5 h-5 text-[#C76714]" />
          <h2 className="font-display text-lg font-bold text-slate-800">3 · Missing photo files</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Finds photos whose image file is <span className="font-semibold">definitely gone</span> from storage (a real "not found" — a temporary storage slowdown is never counted). Flagged photos stop counting as "photographed", can't satisfy a cart's ship-ready check, and appear on your Home as "Photos with missing files". Nothing is deleted; a flag clears itself if the file returns. This also runs automatically every 6 hours.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button data-testid="missing-scan-dry" variant="outline" disabled={msBusy} onClick={() => runMissingScan(false)} className="rounded-lg h-10 border-slate-300">
            {msBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Play className="w-4 h-4 mr-1.5" />}Dry run (look first)
          </Button>
          <Button data-testid="missing-scan-apply" disabled={msBusy} onClick={() => runMissingScan(true)} className="rounded-lg h-10 bg-[#E0781A] hover:bg-[#C76714] text-white">
            {msBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ImageOff className="w-4 h-4 mr-1.5" />}Flag missing photos
          </Button>
        </div>
        {ms && (
          <div data-testid="missing-scan-result" className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-700 mb-2">{ms.apply ? "Applied — flags written" : "Dry run — nothing was written"}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1">
              <StatRow label="Photos checked" value={ms.scanned} />
              <StatRow label={ms.apply ? "Flagged missing" : "Would flag"} value={ms.flagged} tone={ms.flagged ? "red" : "green"} />
              <StatRow label={ms.apply ? "Cleared (came back)" : "Would clear"} value={ms.cleared} tone={ms.cleared ? "green" : "slate"} />
              <StatRow label="Skipped (slowdown)" value={ms.inconclusive} tone={ms.inconclusive ? "amber" : "slate"} />
            </div>
            <div className="mt-2 text-xs font-mono text-slate-500">
              Parts {ms.by_collection?.parts?.flagged ?? 0} · Trucks {ms.by_collection?.donors?.flagged ?? 0} · Lots {ms.by_collection?.capture_batches?.flagged ?? 0}
            </div>
          </div>
        )}
      </div>

      {/* -------- SIGNED-OFF CART PHOTO GAPS -------- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-5">
        <div className="flex items-center gap-2 mb-1">
          <ShoppingCart className="w-5 h-5 text-[#C76714]" />
          <h2 className="font-display text-lg font-bold text-slate-800">4 · Signed-off carts missing a part photo</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">Read-only. Lists carts that were already signed off but whose shipped parts would fail today's photo check — so you can decide re-shoot vs accept per item. Run the missing-photo scan above first for the most accurate results.</p>
        <Button data-testid="gaps-load" disabled={gapBusy} onClick={loadGaps} className="rounded-lg h-10 bg-[#1F1F1F] hover:bg-black text-white mb-3">
          {gapBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileSearch className="w-4 h-4 mr-1.5" />}Review signed-off photo gaps
        </Button>
        {gaps && (
          <div data-testid="gaps-report">
            {gaps.count === 0 ? (
              <div data-testid="gaps-none" className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-700 flex items-center justify-center gap-2">
                <ShieldCheck className="w-5 h-5" />No signed-off carts are missing a part photo.
              </div>
            ) : (
              <>
                <div className="text-sm text-slate-700 mb-3">
                  <span className="font-bold">{gaps.count}</span> cart(s) — {Object.entries(gaps.reason_summary || {}).map(([k, v]) => `${v} ${REASON_LABEL[k] || k}`).join(" · ")}
                </div>
                <div className="space-y-2">
                  {gaps.carts.map((c) => (
                    <div key={c.cart_number} data-testid={`gap-cart-${c.cart_number}`} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono font-semibold text-slate-800">{c.cart_number}</span>
                        <Badge className="rounded-full border-0 bg-slate-100 text-slate-600 text-[10px] font-mono">signed off {fmtDate(c.signed_off_at)}</Badge>
                        {c.signed_off_by && <span className="text-xs text-slate-400">by {c.signed_off_by}</span>}
                      </div>
                      <div className="space-y-0.5">
                        {c.parts.map((p) => (
                          <div key={p.part_id} className="flex items-center justify-between text-sm gap-3">
                            <span className="font-mono text-slate-700">{p.short_code || p.part_id}</span>
                            <span className="text-[#B01E28] text-xs text-right">{REASON_LABEL[p.reason] || p.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* -------- INVENTORY: PARTS AWAITING A SOURCE TRUCK (owner diagnostic) -------- */}
      {canManageParts && (
        <div data-testid="source-tie-section" className="rounded-2xl border border-slate-200 bg-white p-5 mt-5">
          <div className="flex items-center gap-2 mb-1">
            <Link2Off className="w-5 h-5 text-[#C76714]" />
            <h2 className="font-display text-lg font-bold text-slate-800">5 · Parts awaiting a source truck</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">Read-only. Surfaces available parts that have <span className="font-semibold">no source tie</span> (no donor truck and no capture lot) — the exact, non-waivable rule the cart sign-off gate blocks on. Catching them here means fixing the tie on a quiet afternoon, not at the counter with a customer waiting. <span className="font-semibold">Legacy parts are excluded</span> — their lot is a valid source, so real legacy inventory never appears here. Sample/demo data is excluded too.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            <Button data-testid="source-tie-counts-btn" variant="outline" disabled={countsBusy} onClick={loadCounts} className="rounded-lg h-10 border-slate-300">
              {countsBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ListChecks className="w-4 h-4 mr-1.5" />}Load counts
            </Button>
            <Button data-testid="source-tie-list-btn" disabled={awaitBusy} onClick={loadAwaiting} className="rounded-lg h-10 bg-[#1F1F1F] hover:bg-black text-white">
              {awaitBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileSearch className="w-4 h-4 mr-1.5" />}Show the list
            </Button>
          </div>

          {counts && (
            <div data-testid="source-tie-counts" className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Legacy (lot IS its source)</div>
                <StatRow label="Total legacy" value={counts.legacy.total} />
                <StatRow label="No tie at all" value={counts.legacy.no_tie} tone={counts.legacy.no_tie ? "amber" : "green"} />
                <StatRow label="…of those, available" value={counts.legacy.no_tie_available} tone={counts.legacy.no_tie_available ? "amber" : "green"} />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Non-legacy (needs a truck)</div>
                <StatRow label="Total non-legacy" value={counts.non_legacy.total} />
                <StatRow label="No tie at all" value={counts.non_legacy.no_tie} tone={counts.non_legacy.no_tie ? "red" : "green"} />
                <StatRow label="…of those, available" value={counts.non_legacy.no_tie_available} tone={counts.non_legacy.no_tie_available ? "red" : "green"} />
              </div>
              <div className="sm:col-span-2 text-[11px] text-slate-400 mt-1">Real inventory only — sample/demo data excluded.</div>
            </div>
          )}

          {awaiting && (
            <div data-testid="source-tie-list">
              {awaiting.total === 0 ? (
                <div data-testid="source-tie-none" className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-700 flex items-center justify-center gap-2">
                  <ShieldCheck className="w-5 h-5" />No parts awaiting a source truck — you're at zero.
                </div>
              ) : (
                <>
                  <div className="text-sm text-slate-700 mb-3"><span className="font-bold">{awaiting.total}</span> part(s) awaiting a source truck{awaiting.shown < awaiting.total ? ` · showing first ${awaiting.shown}` : ""}. Assign each to its truck (scan-assign on the part) — batch-mates likely need the same.</div>
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    {awaiting.items.map((p) => (
                      <div key={p.id} data-testid={`awaiting-part-${p.short_code}`} className="flex items-center justify-between gap-3 px-3 py-2 border-t border-slate-100 first:border-t-0 hover:bg-slate-50/70">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-slate-800 truncate">{p.name || <span className="italic text-slate-400">Unnamed part</span>}</div>
                          <div className="text-[11px] font-mono text-slate-400 flex items-center gap-2 flex-wrap">
                            <span>{p.short_code}</span>
                            <span>· created {fmtDate(p.created_at)}</span>
                            {p.spawn_batch_id && <Badge className="rounded-md border-0 bg-orange-100 text-[#C76714] text-[9px] font-mono">batch {p.spawn_batch_id}</Badge>}
                          </div>
                        </div>
                        <a href={`/parts/${p.id}`} data-testid={`awaiting-open-${p.short_code}`} className="text-xs font-semibold text-[#E0781A] hover:underline shrink-0">Open →</a>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* -------- 6 · INSTALLED PARTS STILL READING AVAILABLE (stale status — double-sell risk) -------- */}
      {canManageParts && (
        <div data-testid="stale-status-section" className="rounded-2xl border border-slate-200 bg-white p-5 mt-5">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5 text-[#B01E28]" />
            <h2 className="font-display text-lg font-bold text-slate-800">6 · Installed parts still reading Available</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">Read-only. Finds parts a <span className="font-semibold">completed or signed-off</span> cart says were shipped, but whose record still reads <span className="font-semibold">available/reserved</span> — a legacy record from before the current ship logic. This is the double-sell risk: an installed part reading Available can get sold or picked twice. <span className="font-semibold">Genuinely returned parts are excluded</span> (they correctly read available). No fix is applied here — this is the dry-run count so you can see the scope first.</p>
          <Button data-testid="stale-status-scan-btn" disabled={staleBusy} onClick={loadStale} className="rounded-lg h-10 bg-[#1F1F1F] hover:bg-black text-white mb-3">
            {staleBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileSearch className="w-4 h-4 mr-1.5" />}Scan for stale statuses
          </Button>
          {stale && (
            <div data-testid="stale-status-result">
              {stale.count === 0 ? (
                <div data-testid="stale-status-none" className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-700 flex items-center justify-center gap-2">
                  <ShieldCheck className="w-5 h-5" />Clean — no shipped part is reading Available. Every completed/signed part reflects reality.
                </div>
              ) : (
                <>
                  <div className="text-sm text-slate-700 mb-3"><span className="font-bold text-[#B01E28]">{stale.count}</span> part(s) shipped on a closed cart but still reading available/reserved. Each should be <span className="font-mono">installed</span> or <span className="font-mono">sold</span>. Review, then approve the status-only correction.</div>
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    {stale.parts.map((p) => (
                      <div key={p.part_id} data-testid={`stale-part-${p.short_code}`} className="flex items-center justify-between gap-3 px-3 py-2 border-t border-slate-100 first:border-t-0 hover:bg-slate-50/70">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-slate-800 truncate">{p.name || <span className="italic text-slate-400">Unnamed</span>} <span className="font-mono text-[11px] text-slate-400">{p.tag_code || p.short_code}</span></div>
                          <div className="text-[11px] font-mono text-slate-400 flex items-center gap-2 flex-wrap">
                            <Badge className="rounded-md border-0 bg-red-100 text-[#B01E28] text-[9px] font-mono uppercase">reads {p.status}</Badge>
                            <span>→ should be {p.should_be}</span>
                            <span>· {p.cart_number} ({p.cart_status})</span>
                            {p.has_exit_record && <Badge className="rounded-md border-0 bg-amber-100 text-amber-700 text-[9px] font-mono">had exit record — review</Badge>}
                            {p.was_returned_before && <Badge className="rounded-md border-0 bg-sky-100 text-sky-700 text-[9px] font-mono">returned before — review</Badge>}
                          </div>
                        </div>
                        <a href={`/parts/${p.part_id}`} data-testid={`stale-open-${p.short_code}`} className="text-xs font-semibold text-[#E0781A] hover:underline shrink-0">Open →</a>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* -------- 7 · PHOTOS WITH NO DURABLE BACKUP COPY (PHOTO-DUR-01) -------- */}
      {canManageBackups && (
        <div data-testid="durability-section" className="rounded-2xl border border-[#B01E28]/30 bg-white p-5 mt-5">
          <div className="flex items-center gap-2 mb-1">
            <HardDriveDownload className="w-5 h-5 text-[#B01E28]" />
            <h2 className="font-display text-lg font-bold text-slate-800">7 · Photos with no durable backup copy</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">A photo with <span className="font-semibold">no durable copy</span> exists only on this server's temporary disk — the next redeploy wipes it and the picture is gone for good. <span className="font-semibold">Archived photos are counted here too:</span> the 37 photos lost on DNR-00002 were all archived, which is exactly why nothing warned about them. Repair uploads the bytes to durable cloud storage. A photo whose file is <span className="font-semibold">already gone everywhere</span> is marked LOST — kept, labelled, never deleted, and never offered as a restore that can't happen.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            <Button data-testid="durability-check" variant="outline" disabled={durBusy} onClick={loadDurability} className="rounded-lg h-10 border-slate-300">
              {durBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileSearch className="w-4 h-4 mr-1.5" />}Check durability
            </Button>
            <Button data-testid="durability-repair" disabled={durBusy || !dur?.total} onClick={repairDurability} className="rounded-lg h-10 bg-[#B01E28] hover:bg-[#8f1820] text-white">
              {durBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <HardDriveDownload className="w-4 h-4 mr-1.5" />}Make them durable
            </Button>
          </div>
          {dur && (
            <div data-testid="durability-result">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-3 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1">
                <StatRow label="No durable copy" value={dur.total} tone={dur.total ? "red" : "green"} />
                <StatRow label="…bytes still on disk (savable)" value={dur.counts?.bytes_still_on_disk ?? 0} tone={dur.counts?.bytes_still_on_disk ? "amber" : "slate"} />
                <StatRow label="…already gone (lost)" value={dur.lost_total ?? 0} tone={dur.lost_total ? "red" : "green"} />
                <StatRow label="Live photos at risk" value={dur.counts?.live ?? 0} tone={dur.counts?.live ? "red" : "green"} />
                <StatRow label="Archived photos at risk" value={dur.counts?.archived ?? 0} tone={dur.counts?.archived ? "amber" : "green"} />
                <StatRow label="Files in library" value={dur.files_total ?? 0} />
              </div>
              {dur.total === 0 ? (
                <div data-testid="durability-none" className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-700 flex items-center justify-center gap-2">
                  <ShieldCheck className="w-5 h-5" />
                  {dur.lost_total
                    ? `Every photo that still exists has a durable copy. ${dur.lost_total} photo record${dur.lost_total === 1 ? " is" : "s are"} marked LOST — the image file is gone for good and needs re-shooting. The records are kept, not deleted.`
                    : "Every photo in the library has a durable copy."}
                </div>
              ) : (
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-100 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    Showing {dur.shown} of {dur.total} at-risk file{dur.total === 1 ? "" : "s"}
                  </div>
                  {dur.items.map((f) => (
                    <div key={f.file_id} data-testid={`durability-file-${f.file_id.slice(0, 8)}`} className="flex items-center justify-between gap-3 px-3 py-2 border-t border-slate-100 text-sm">
                      <span className="min-w-0 truncate">
                        <span className="font-mono font-semibold text-slate-800">{f.owner?.label || "(no record points at this file)"}</span>
                        <span className="text-[11px] font-mono text-slate-400 ml-2">{f.owner?.kind || "orphan"}{f.owner?.is_archived ? " · archived" : ""}</span>
                      </span>
                      <Badge className={`rounded-md border-0 text-[9px] font-mono shrink-0 ${f.bytes_on_disk ? "bg-amber-100 text-[#C76714]" : "bg-red-100 text-[#B01E28]"}`}>
                        {f.bytes_on_disk ? "savable now" : "bytes gone"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <PhotoLightbox photos={shot?.list || []} index={shot ? shot.idx : null}
        onIndex={(i) => setShot((v) => v && { ...v, idx: i })} onClose={() => setShot(null)}
        testidPrefix="dup-photo-viewer" />
    </div>

  );
}
